import { RefElement } from 'widgets/toolbox/RefElement';
import { isSmallView } from 'widgets/toolbox/viewtype';

declare global {
    interface Window {
        dimensionalDropdownKeys: string;
        dimensionalDropdownValues: string;
    }
}

declare const window: ExtendedWindow;

/**
 * @description Returns Dimensional Dropdown Localised Value
 * @param key - DimensionalDropdown Key Value
 * @returns Dimensional Dropdown Localised Value
 */
export function findDropDownValue(key) {
    if (!window.dimensionalDropdownKeys || !window.dimensionalDropdownValues) {
        return key;
    }

    const keyIndex = window.dimensionalDropdownKeys.split(';').indexOf(key);

    return (keyIndex >= 0 && window.dimensionalDropdownValues.split(';')[keyIndex]) || key;
}

/**
 * @description Returns decoded HTML string
 * @param str - encoded string
 * @returns plain text string
 */
export function htmlDecode(str) {
    const doc = new DOMParser().parseFromString(str, 'text/html');

    return doc.documentElement.textContent;
}

/**
 * @description Check if browser is Mobile Safari
 * @returns true if browser is Mobile Safari
 */
export function isMobileSafari() {
    const userAgent = navigator.userAgent;

    return /iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(userAgent);
}

/**
 * @description Check if browser is Desktop Safari
 * @returns true if browser is Desktop Safari
 */
export function isDesktopSafari() {
    const userAgent = navigator.userAgent;

    return /Macintosh[^]*?Version\/\d+(\.\d+)*[^]*?Safari/i.test(userAgent) && !/(Chrome|CriOS|Edg)/i.test(userAgent);
}

/**
 * Determines whether the current user's operating system is macOS.
 *
 * This function checks the `navigator.userAgentData` object for modern browsers
 * to identify the platform. If `userAgentData` is unavailable, it falls back
 * to using the `navigator.userAgent` string for older browsers.
 *
 * @returns  `true` if the user's operating system is macOS, otherwise `false`.
 */
export function isMacOs() {
    let isMac = false;

    if (
        'userAgentData' in navigator &&
        navigator.userAgentData &&
        typeof navigator.userAgentData === 'object' &&
        'platform' in navigator.userAgentData &&
        navigator.userAgentData.platform
    ) {
        // Modern way
        isMac = typeof navigator.userAgentData.platform === 'string' && navigator.userAgentData.platform.toLowerCase().includes('mac');
    } else {
        // Fallback for older browsers
        isMac = /mac/i.test(navigator.userAgent);
    }

    return isMac;
}

/**
 * Calculates and retrieves the height of the header element.
 *
 * @returns The height of the header element in pixels.
 */
export function getHeaderHeight(isCountMobileSearchHeight = false) {
    const header = document.getElementById('headercontent');
    const mobileSearchHeight = 55;
    let headerHeight = 0;

    if (header) {
        headerHeight = header.offsetHeight;
    }

    if (isCountMobileSearchHeight && header?.dataset.hasMobileSearch && isSmallView()) {
        headerHeight += mobileSearchHeight;
    }

    return headerHeight;
}

/**
 * @description Check if string is Base64 encoded
 * @param str - string to check
 * @returns true if string is Base64 encoded, false otherwise
 */
export function isBase64Encoded(str: string): boolean {
    if (str.includes('?') || str.includes('&') || str.includes('#')) {
        return false;
    }

    const urlRegex = /^(http|https):\/\//;

    if (urlRegex.test(str)) {
        return false;
    }

    if (str.length % 4 === 1) {
        return false;
    }

    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;

    if (!base64Regex.test(str)) {
        return false;
    }

    try {
        atob(str);

        return true;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
        return false;
    }
}

/**
 * Validates and decodes a given URL path string.
 *
 * This function checks if the provided path contains percent-encoded characters
 * and attempts to decode it. If decoding fails, the original path is returned.
 * Additionally, it ensures that the decoded path contains only Latin characters,
 * digits, underscores, hyphens, or slashes. If the path contains any other
 * characters, `null` is returned.
 *
 * @param path - The URL path string to validate and decode. Can be `null`.
 * @returns The decoded path if valid, the original path if decoding is not needed
 * or fails, or `null` if the path contains invalid characters.
 */
function validateAndDecodePath(path: string | null): string | null {
    if (!path || !/%[0-9a-f]{2}/i.test(path)) {
        return path;
    }

    try {
        path = decodeURIComponent(path);
    } catch {
        // ignore decode errors, keep original
    }

    const isLatin = /^[a-z0-9_/-]*$/i.test(path ?? '');

    return isLatin ? path : null;
}

/**
 * Extracts and returns the path of a URL starting from the country code
 * If the URL does not match the expected format, returns null
 */
export function extractPath(url: string, isLatinOnly: boolean = true): string | null {
    const regex = /(?:https?:\/\/)?(?:[a-zA-Z0-9.-]+)?\/([a-zA-Z]{2,4}(?:-[a-zA-Z]{2})?)\/(.*)/;
    const match = url.match(regex);

    if (!match) {
        return null;
    }

    let path = `/${match[2]}`;

    if (path.endsWith('/')) {
        path = path.slice(0, -1);
    }

    return isLatinOnly ? validateAndDecodePath(path) : path;
}

/**
 * Emits a click event with a specific event type and URL path
 *
 * @param elem - The element that was clicked
 * @param eventType - The type of event to emit
 */
export function trackLink(
    this: { eventBus: () => { emit: (event: string, url: string | null) => void } },
    elem: RefElement<HTMLElement>,
    eventType: string
) {
    const url = extractPath(elem.attr('href') ?? '');

    this.eventBus().emit(`${eventType}.click`, url);
}

/**
 * @description Get the submenu element
 * @returns Submenu element
 */
export type TDataTauHelpers = {
    isViewAllTau: (t: string) => boolean;
    formatViewAllTau: (t: string) => string;
    stripMenuItemPrefix: (t: string) => string;
};

export const tauHelpers: TDataTauHelpers = {
    isViewAllTau: (t: string) => t.includes('_viewAll_'),
    formatViewAllTau: (t: string) => {
        const slug = t.split('_viewAll_').pop() ?? '';

        return `${slug}_viewAll`;
    },
    stripMenuItemPrefix: (t: string) => t.replace(/^menu_item_level\d+_/, '')
};

/**
 * Builds a CSS selector string for a given form element, optionally including its parent element's classes.
 *
 * @param form - The form element for which to build the selector
 * @param includeParent - Whether to include the parent element's classes in the selector
 * @returns A CSS selector string
 */
export function buildFormSelector(form: HTMLFormElement, includeParent: boolean): string {
    if (!form) {
        return '';
    }

    const elementClasses = [...form.classList].map(cls => `.${cls}`).join('');

    if (!includeParent) {
        return elementClasses;
    }

    const formParent = form.parentNode as HTMLElement | null;

    if (!formParent) {
        return elementClasses;
    }

    const parentClasses = [...formParent.classList].map(cls => `.${cls}`).join(' ');

    return `${parentClasses} ${elementClasses}`.trim();
}

/**
 * @description Determines if the customer is a member
 * If loyalty enrollment is required, the customer must be an employee or enroll in the loyalty program to be considered a member.
 * Otherwise, the customer is considered a member if they are signed in.
 * @param isLoyaltyEnrollmentRequired Whether loyalty enrollment is required
 * @returns True if the customer is a member, false otherwise
 */
export function isMember(isLoyaltyEnrollmentRequired: boolean): boolean {
    const customerData = window.sfccUnCachedData?.customerData;

    if (customerData?.isEmployee) {
        return true;
    }

    if (isLoyaltyEnrollmentRequired) {
        return customerData?.isLoyaltyMember ?? false;
    } else {
        return !customerData?.isAnonymous;
    }
}

/**
 * @description Determines if the customer is signed in
 * @returns True if the customer is signed in, false otherwise
 */
export function isSignedIn(): boolean {
    return !window.sfccUnCachedData?.customerData?.isAnonymous;
}
